import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { FormInput, UserInformationPanel } from 'src/components/molecules';
import { InviteToCaseModal } from 'src/components/molecules/InviteToCaseModal';
import {
  HeaderSectionTitle,
  HeaderSectionTitleContainer,
} from 'src/components/organismsLegacy/CaseScreenHeader/CaseScreenHeader.styles';
import { ReassignCaseModal } from 'src/components/organismsLegacy/CaseScreenHeader/components';
import { caseLeadUpdated } from 'src/components/pages/Case/actions/caseLeadUpdated';
import { useReassignCaseLeadMutation } from 'src/graphql';
import { AppDispatch } from 'src/store';
import { FormInputVariant } from 'src/types';
import { ReviewCaseTeamMemberModal } from '../ReviewCaseTeamMemberModal';
import {
  CaseLeadDropdownContainer,
  CaseScreenHeaderDetailsColumnNewTeamPanel,
  HeaderSectionContainer,
  HeaderSectionCount,
  InnerContainer,
} from './CaseTeamPanel.styles';
import { caseTeamPanel } from './CaseTeamPanel.vm';

export const CaseTeamPanel = () => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const {
    canEditCaseLead,
    canInviteToCase,
    canViewInvitationDetails,
    canViewInviteButton,
    availableCaseLeads,
    caseTeamListItems,
    caseTeamMembersCount,
    caseLeadLabel,
    caseLeadId,
    caseId,
  } = useSelector(caseTeamPanel);

  const [updatedCaseManager, setUpdatedCaseManager] = useState<
    number | undefined
  >();
  const [stepperModalOpen, setStepperModalOpen] = useState(false);
  const [reviewPanelUserId, setReviewPanelUserId] = useState<number | null>(
    null,
  );

  const displayCaseLead = {
    label: caseLeadLabel,
    value: caseLeadId,
  };

  const { mutateAsync: mutateReassignCaseLead } = useReassignCaseLeadMutation();

  const handleReassignCaseLead = async (newCaseLeadId?: number) => {
    dispatch(
      caseLeadUpdated({
        caseId,
        caseLeadId: newCaseLeadId,
        mutateReassignCaseLead,
        queryClient,
      }),
    );

    setUpdatedCaseManager(undefined);
  };

  return (
    <CaseScreenHeaderDetailsColumnNewTeamPanel>
      <InnerContainer>
        <HeaderSectionContainer>
          <HeaderSectionTitleContainer>
            <HeaderSectionTitle>Case team</HeaderSectionTitle>
            <HeaderSectionCount>({caseTeamMembersCount})</HeaderSectionCount>
          </HeaderSectionTitleContainer>
          {canViewInviteButton && (
            <Button
              variant="primary"
              size="small"
              data-testid="invite-to-case-button"
              onClick={() => setStepperModalOpen(true)}
              disabled={!canInviteToCase}
            >
              <StyledIconInButton
                size="small"
                position="left"
                className="uil uil-plus"
              />
              Invite team
            </Button>
          )}
        </HeaderSectionContainer>
        {canEditCaseLead && (
          <CaseLeadDropdownContainer>
            <FormInput
              variant={FormInputVariant.DROPDOWN}
              options={availableCaseLeads}
              value={displayCaseLead}
              onChange={(option) => {
                if (option && 'value' in option && option.value) {
                  if (option.value === caseLeadId) return;

                  setUpdatedCaseManager(Number(option.value));
                }
              }}
              label="Case lead"
              data-testid="reassign-case-lead-dropdown"
              isSearchable
            />
          </CaseLeadDropdownContainer>
        )}
      </InnerContainer>
      <UserInformationPanel
        users={caseTeamListItems}
        onUserSelected={(userId) => {
          if (canViewInvitationDetails) {
            setReviewPanelUserId(userId);
          }
        }}
      />
      <ReassignCaseModal
        setOpen={() => setUpdatedCaseManager(undefined)}
        open={updatedCaseManager !== undefined}
        onConfirm={() => {
          handleReassignCaseLead(updatedCaseManager);
        }}
        onCancel={() => {
          setUpdatedCaseManager(undefined);
        }}
      />
      <InviteToCaseModal
        open={stepperModalOpen}
        setOpen={setStepperModalOpen}
      />
      <ReviewCaseTeamMemberModal
        open={reviewPanelUserId !== null}
        closeModal={() => setReviewPanelUserId(null)}
        caseId={caseId}
        userId={reviewPanelUserId}
        showNoteTop={false}
      />
    </CaseScreenHeaderDetailsColumnNewTeamPanel>
  );
};
